import React, { useState, useEffect } from "react";
import { api } from "../api";
import Icon from "./Icon";
import { useAssetStore } from "../store/useAssetStore";

export default function DemoConsoleModal({ isOpen, onClose }) {
  const [targets, setTargets] = useState(null);
  const [selectedHostname, setSelectedHostname] = useState("");
  const [loadingAction, setLoadingAction] = useState(null);
  const [targetError, setTargetError] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const fetchAllData = useAssetStore((state) => state.fetchAllData);

  const handleClose = () => {
    setFeedbackMessage(null);
    if (onClose) onClose();
  };

  useEffect(() => {
    setFeedbackMessage(null);
    if (isOpen) {
      setTargets(null);
      setSelectedHostname("");
      setTargetError(null);
      api("/demo/targets")
        .then((data) => {
          if (!Array.isArray(data)) {
            throw new Error(
              "Unexpected response loading simulator targets. Check the API base URL and /demo proxy configuration.",
            );
          }
          const nextTargets = data;
          setTargets(nextTargets);
          if (nextTargets.length > 0) {
            setSelectedHostname((current) =>
              nextTargets.some((target) => target.hostname === current)
                ? current
                : nextTargets[0].hostname,
            );
          } else {
            setSelectedHostname("");
          }
        })
        .catch((err) => {
          setTargets([]);
          setSelectedHostname("");
          setTargetError(err?.message || "Unable to load simulator targets.");
        });
    } else {
      setFeedbackMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedDevice =
    targets?.find((t) => t.hostname === selectedHostname) || targets?.[0];
  const actionDisabled = loadingAction !== null || !selectedHostname;
  const isNetworkDevice =
    selectedDevice &&
    ["router", "switch", "firewall", "gateway", "load_balancer"].includes(
      (selectedDevice.asset_category || "").toLowerCase(),
    );

  const actions = [
    {
      action: "cpu_spike",
      label: isNetworkDevice ? "Inject High Load / CPU Spike (85%)" : "Inject Compute CPU Load (85%)",
      description: isNetworkDevice
        ? "Increases load utilization while keeping router Active & Monitored."
        : "Generates heavy compute CPU load on the target host.",
      icon: "cpu",
      targets: "all",
      tone: "warning",
    },
    {
      action: "router_down",
      label: "Inject Router Outage (Offline)",
      description: "Simulates a network appliance interface outage.",
      icon: "alert-triangle",
      targets: "network",
      tone: "danger",
    },
    {
      action: "latency_spike",
      label: "Inject WAN Latency (185ms)",
      description: "Simulates network congestion and response latency degradation.",
      icon: "clock",
      targets: "network",
      tone: "warning",
    },
    {
      action: "reset_healthy",
      label: isNetworkDevice ? "Restore Appliance Health (100%)" : "Restore Host Health (100%)",
      description: isNetworkDevice
        ? "Restores active link status and healthy SNMP reachability."
        : "Terminates stress tasks and restores normal compute utilization.",
      icon: "check-circle",
      targets: "all",
      tone: "success",
    },
    {
      action: "oom_kill",
      label: "Out of Memory (OOM) Kill",
      description: "Runs a memory-limited process that is killed at its 96 MB limit.",
      icon: "database",
      targets: "server",
      tone: "danger",
    },
    {
      action: "segfault",
      label: "Segmentation Fault",
      description: "Crashes an isolated test process on the selected server.",
      icon: "zap",
      targets: "server",
      tone: "danger",
    },
    {
      action: "high_system_load",
      label: "High System Load",
      description: "Runs CPU workers, capped at two cores for up to 110 seconds.",
      icon: "activity",
      targets: "server",
      tone: "warning",
    },
  ].map((action) => ({ targets: "all", tone: "default", ...action }));

  const handleTrigger = async (actionType, actionLabel) => {
    if (!selectedHostname || loadingAction !== null) return;
    setLoadingAction(actionType);
    setFeedbackMessage(null);
    try {
      const res = await api("/demo/trigger", {
        method: "POST",
        body: JSON.stringify({
          hostname: selectedHostname,
          action: actionType,
        }),
      });
      const eventSummary = res?.event_type === "log"
        ? `${res.action || actionType} log event`
        : res?.event_type === "metric"
          ? `${res.metric || actionType}: ${res.value ?? "updated"}`
          : null;
      setFeedbackMessage({
        type: "success",
        text:
          res?.message ||
          res?.detail ||
          `Fault Injection '${actionLabel}' executed for ${res?.hostname || selectedHostname}.${eventSummary ? ` ${eventSummary}.` : ""}`,
      });

      if (res?.hostname && (res.device_status !== undefined || res.specs)) {
        const telemetry = {};
        if (res.device_status !== undefined) telemetry.status = res.device_status;
        if (res.specs) {
          telemetry.specs = res.specs;
          if (res.specs.health_score !== undefined) telemetry.health_score = res.specs.health_score;
          if (res.specs.ping_latency_ms !== undefined) telemetry.ping_latency_ms = res.specs.ping_latency_ms;
        }
        useAssetStore.getState().updateLiveTelemetry(res.hostname, telemetry);
      }

      // Force real-time synchronization across all stores & views
      await fetchAllData(true);
    } catch (err) {
      setFeedbackMessage({
        type: "error",
        text:
          err?.message || `Failed to execute Fault Injection '${actionLabel}'.`,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#FFFFFF",
          borderRadius: 12,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#F8FAFC",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#E0E7FF",
                color: "#4338CA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="sliders" size={18} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#0F172A",
                  margin: 0,
                }}
              >
                Infrastructure Resilience & Fault Injector Console
              </h3>
              <p style={{ fontSize: 12, color: "#64748B", margin: 0 }}>
                Inject hardware fault scenarios and WAN latency to validate
                real-time AIOps telemetry response.
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#64748B",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* Target Infrastructure Selector */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#475569",
                display: "block",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Target Infrastructure Appliance
            </label>
            <select
              value={selectedHostname}
              onChange={(e) => setSelectedHostname(e.target.value)}
              disabled={actionDisabled}
              aria-label="Target hostname"
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                fontSize: 14,
                fontWeight: 600,
                color: "#0F172A",
                background: "#FFFFFF",
                outline: "none",
              }}
            >
              {targets === null ? (
                <option value="">Loading targets...</option>
              ) : targets.length === 0 ? (
                <option value="">
                  No candidate infrastructure devices found
                </option>
              ) : (
                targets.map((t) => (
                  <option key={t.hostname} value={t.hostname}>
                    {t.hostname} - {t.name} ({t.ip_address || t.hostname}) [
                    {(t.asset_category || "server").toUpperCase()}] (Status:{" "}
                    {(t.status || "active").toUpperCase()})
                  </option>
                ))
              )}
            </select>
            {targetError && (
              <div role="alert" style={{ marginTop: 8, color: "#991B1B", fontSize: 13 }}>
                {targetError}
              </div>
            )}
          </div>

          {/* All supported simulator actions in one target-aware list */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#475569",
                display: "block",
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Fault Injection Actions
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              {actions
                .filter((action) => action.targets === "all" || action.targets === (isNetworkDevice ? "network" : "server"))
                .map(({ action, label, description, icon, tone }) => {
                  const colors = {
                    warning: { border: "#FED7AA", background: "#FFF7ED", color: "#C2410C" },
                    danger: { border: "#FCA5A5", background: "#FEF2F2", color: "#991B1B" },
                    success: { border: "#A7F3D0", background: "#ECFDF5", color: "#065F46" },
                    default: { border: "#CBD5E1", background: "#F8FAFC", color: "#334155" },
                  }[tone];
                  return (
                    <button
                      key={action}
                      disabled={actionDisabled}
                      onClick={() => handleTrigger(action, label)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "14px 16px",
                        borderRadius: 8,
                        border: "1px solid " + colors.border,
                        background: colors.background,
                        color: colors.color,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <Icon name={icon} size={20} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{label}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: "#64748B", marginTop: 2 }}>
                          {description}
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
          {/* Feedback Alert Message */}
          {loadingAction && (
            <div role="status" style={{ color: "#475569", fontSize: 13, fontWeight: 600 }}>
              Executing {loadingAction.replace(/_/g, " ")} for {selectedHostname}...
            </div>
          )}
          {feedbackMessage && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                background:
                  feedbackMessage.type === "success" ? "#F0FDF4" : "#FEF2F2",
                color:
                  feedbackMessage.type === "success" ? "#166534" : "#991B1B",
                border: `1px solid ${
                  feedbackMessage.type === "success" ? "#BBF7D0" : "#FECACA"
                }`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Icon
                name={
                  feedbackMessage.type === "success" ? "check" : "alert-circle"
                }
                size={16}
              />
              <span>{feedbackMessage.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
